import { DiscordIcon } from "@moe/oss/ui/discord-icon";
/**
 * If this is true, the *entire* app will not be rendered, and instead  we show the maintenance page.
 */
export const IS_MAINTENANCE = false;
/**
 * Specifies the maintenance duration or an absolute return date.
 * * You can use natural language durations such as * - "1 hour" * - "1 hour 30 minutes"
 * - "2h"
 * - "45 minutes"
 *
 * Or provide an ISO-8601 date string, for example:
 * - "2025-02-14T14:52:19.893Z"
 */
const RETURN_TIME = "1 hour";

// Provided by Vite `define` during build.
// See @vite.config.ts
declare const __BUILD_TIME__: number;

/**
 * Small utility to convert a natural-language duration (e.g. "1 hour 30 minutes") to milliseconds.
 * Only the units days, hours, minutes and seconds are supported.
 * If the string cannot be parsed the function returns 0 so that callers can fall back to other strategies.
 * @example
 * ```tsx
 * const duration = parseDuration("1 hour");
 * console.log(duration); // 3600000
 * ```
 */
const parseDuration = (input: string): number => {
  if (!input) return 0;
  input = input.trim();
  const regex = /(\d+(?:\.\d+)?)\s*(d|day|days|h|hr|hour|hours|m|min|minute|minutes|s|sec|second|seconds)/gi;
  let totalMs = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(input)) !== null) {
    const value = parseFloat(match[1]);
    const unit = match[2].toLowerCase();

    switch (unit) {
      case "d":
      case "day":
      case "days":
        totalMs += value * 86_400_000; // 24 * 60 * 60 * 1000
        break;
      case "h":
      case "hr":
      case "hour":
      case "hours":
        totalMs += value * 3_600_000; // 60 * 60 * 1000
        break;
      case "m":
      case "min":
      case "minute":
      case "minutes":
        totalMs += value * 60_000; // 60 * 1000
        break;
      case "s":
      case "sec":
      case "second":
      case "seconds":
        totalMs += value * 1_000; // 1000
        break;
      default:
        break;
    }
  }

  return totalMs;
};

/**
 * Interprets the provided specification. If it looks like a valid ISO-8601 timestamp
 * it is used as-is; otherwise we treat the spec as a duration relative to the build
 * time. Using the build timestamp instead of `Date.now()` prevents the maintenance
 * window from sliding forward each time the user reloads the page.
 */
const computeReturnTime = (spec: string): Date | undefined => {
  const isoDate = new Date(spec);
  if (!isNaN(isoDate.getTime())) return isoDate;
  const durationMs = parseDuration(spec);
  if (durationMs > 0) return new Date(__BUILD_TIME__ + durationMs);
  return undefined;
};

/**
 * Format the return time as a local time.
 * @returns The formatted time or undefined if the time cannot be computed.
 * @example
 * ```tsx
 * const time = formatLocalTime();
 * console.log(time); // 8:20 PM GMT-4
 * ```
 */
const formatLocalTime = () => {
  const date = computeReturnTime(RETURN_TIME);
  if (date) {
    return new Intl.DateTimeFormat("default", {
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short"
    }).format(date);
  }
  return undefined;
};

/**
 * The time in the user's timezone we'll be back at.
 * Ex: "8:20 PM GMT-4"
 */
const returnLocalTime = formatLocalTime();

export const MaintenancePage = () => {
  return (
    <div className="bg-background flex min-h-screen w-full flex-col items-center justify-center p-4 font-semibold">
      <div className="max-w-md text-center">
        <h1 className="text-tx-primary mb-4 text-4xl font-bold">Under Maintenance</h1>
        <div className="my-8">
          <img
            src="https://media1.tenor.com/m/kuB7pt2Mpf4AAAAd/neko.gif"
            alt="Cute cat animation"
            className="border-tx-primary mx-auto rounded-full border-8 shadow-lg"
          />
        </div>
        {returnLocalTime && (
          <p className="text-tx-tertiary mb-4 text-lg">{`will be back at around ${returnLocalTime}`}</p>
        )}
        <div className="text-tx-tertiary mt-8 space-y-2 text-2xl">
          <p className="text-sm">PLEASE join the Discord for status updates. (づ｡◕‿‿◕｡)づ</p>
        </div>
        <a
          href="https://discord.gg/CNGAZrahmA"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center px-6 py-3 text-white transition-colors"
        >
          <DiscordIcon className="mr-2 h-12 w-12" />
          Join Discord
        </a>
      </div>
    </div>
  );
};
